import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { Robot, Plus, Play, FloppyDisk, X, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Agents() {
  const { data, loading, reload } = useFetch(() => api.get("/agents").then((r) => r.data), []);
  const [models, setModels] = useState({ openai: [], anthropic: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    api.get("/agents/models").then(({ data }) => setModels(data));
  }, []);

  const selected = (data || []).find((a) => a.id === selectedId) || (data || [])[0];

  return (
    <>
      <PageHeader
        label="Prompt management"
        title="Agentes IA"
        description="Editores configurables: system prompts, reglas de comportamiento, temas prohibidos, proveedor y modelo."
      >
        <button onClick={() => setShowCreate(true)} data-testid="create-agent-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={16} /> Nuevo agente
        </button>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* List */}
          <Card className="p-3 lg:col-span-1" testid="agents-list">
            {loading && <div className="text-carbon-500 p-3 text-sm">Cargando...</div>}
            {(data || []).map((a) => (
              <button key={a.id} onClick={() => setSelectedId(a.id)}
                data-testid={`agent-item-${a.id}`}
                className={`w-full text-left p-3 rounded border-l-2 mb-1 transition-colors ${selected?.id === a.id ? "bg-page border-terracotta" : "border-transparent hover:bg-page"}`}>
                <div className="flex items-center gap-2">
                  <Robot size={14} className="text-terracotta" />
                  <span className="font-medium text-sm">{a.name}</span>
                </div>
                <div className="text-[10px] text-carbon-500 mt-1 font-mono">{a.provider}/{a.model}</div>
                <div className="flex items-center gap-1 mt-1.5">
                  <Badge tone={a.active ? "forest" : "neutral"}>{a.active ? "activo" : "off"}</Badge>
                  <span className="text-[10px] text-carbon-400">v{a.version}</span>
                </div>
              </button>
            ))}
            {data && data.length === 0 && <div className="text-xs text-carbon-400 p-3">Sin agentes</div>}
          </Card>

          {/* Editor */}
          <div className="lg:col-span-3">
            {selected ? <AgentEditor agent={selected} models={models} onSaved={reload} /> : (
              <Card className="p-10 text-center text-carbon-400">Selecciona un agente</Card>
            )}
          </div>
        </div>
      </PageBody>
      {showCreate && <CreateAgentModal models={models} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}
    </>
  );
}

function AgentEditor({ agent, models, onSaved }) {
  const [form, setForm] = useState(agent);
  const [testInput, setTestInput] = useState("Hola, quiero unirme a Heroes Migrantes");
  const [testOutput, setTestOutput] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setForm(agent); setTestOutput(null); }, [agent.id]);

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el agente "${agent.name}"?\n\nEsto NO se puede deshacer. Si el agente está siendo usado en algún stage de pipeline, el sistema bloqueará el borrado.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/agents/${agent.id}`);
      toast.success("Agente eliminado");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally { setDeleting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/agents/${agent.id}`, {
        name: form.name, description: form.description,
        system_prompt: form.system_prompt,
        behavioral_rules: form.behavioral_rules,
        forbidden_topics: form.forbidden_topics,
        tone: form.tone, provider: form.provider, model: form.model,
        temperature: form.temperature, active: form.active,
        max_messages: parseInt(form.max_messages) || 1,
        lead_origin: form.lead_origin || null,
        origin_keywords: form.origin_keywords || [],
        origin_stage_keys: form.origin_stage_keys || [],
      });
      toast.success("Agente guardado");
      onSaved();
    } catch (err) { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setTestOutput(null);
    try {
      const { data } = await api.post(`/agents/${agent.id}/test`, { user_message: testInput });
      setTestOutput(data);
    } catch (err) { toast.error("Error en test"); } finally { setTesting(false); }
  };

  const availableModels = models[form.provider] || [];

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="section-label">Editando</div>
            <h2 className="font-heading text-2xl">{form.name}</h2>
            <div className="text-xs text-carbon-500">versión {form.version} · {form.history?.length || 0} cambios previos</div>
          </div>
          <div className="flex gap-2">
            <button onClick={remove} disabled={deleting || saving} data-testid="delete-agent-btn"
              className="bg-signal/10 border border-signal text-signal px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-signal/20 disabled:opacity-60">
              <Trash size={14} /> {deleting ? "Eliminando..." : "Eliminar"}
            </button>
            <button onClick={save} disabled={saving} data-testid="save-agent-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
              <FloppyDisk size={14} /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <label className="block">
            <span className="section-label">Provider</span>
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
            </select>
          </label>
          <label className="block">
            <span className="section-label">Modelo</span>
            <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="agent-model-select" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono">
              {availableModels.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="section-label">Temperature</span>
            <input type="number" step="0.1" min="0" max="2" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
          </label>
        </div>

        <label className="block mb-4">
          <span className="section-label">Descripción</span>
          <input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
        </label>

        <label className="block mb-4">
          <span className="section-label">System Prompt</span>
          <textarea value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} data-testid="agent-system-prompt" rows={10} className="mt-1 w-full bg-carbon-900 text-carbon-100 border border-carbon-800 rounded px-3 py-2 text-sm font-mono" />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="section-label">Reglas de comportamiento (una por línea)</span>
            <textarea
              value={(form.behavioral_rules || []).join("\n")}
              onChange={(e) => setForm({ ...form, behavioral_rules: e.target.value.split("\n").filter(Boolean) })}
              rows={5}
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="block">
            <span className="section-label">Temas prohibidos (uno por línea)</span>
            <textarea
              value={(form.forbidden_topics || []).join("\n")}
              onChange={(e) => setForm({ ...form, forbidden_topics: e.target.value.split("\n").filter(Boolean) })}
              rows={5}
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
        </div>
      </Card>

      <Card className="p-5" data-testid="agent-advanced-config">
        <div className="section-label mb-1">Mensajes de salida & Origen del lead</div>
        <p className="text-xs text-carbon-500 mb-4">
          Define en cuántos mensajes responde el agente y qué origen de lead atiende (para responder distinto según de dónde viene).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="section-label">Dividir salida en N mensajes (1–5)</span>
            <input type="number" min="1" max="5" value={form.max_messages || 1}
              onChange={(e) => setForm({ ...form, max_messages: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) })}
              data-testid="agent-max-messages" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            <span className="text-[10px] text-carbon-500 mt-1 block">
              Con valor &gt;1, escribe la respuesta con prefijos <span className="font-mono">mensaje 1:</span>, <span className="font-mono">mensaje 2:</span> en el prompt. El sistema los separa y envía como mensajes distintos.
            </span>
          </label>
          <label className="block">
            <span className="section-label">Origen del lead que atiende</span>
            <input value={form.lead_origin || ""} onChange={(e) => setForm({ ...form, lead_origin: e.target.value })}
              placeholder="ej: webinar, evento_iglesia"
              data-testid="agent-lead-origin" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            <span className="text-[10px] text-carbon-500 mt-1 block">Vacío = agente genérico. Si lo llenas, este agente toma el control cuando el lead tiene ese origen.</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="section-label">Palabras clave del origen (una por línea)</span>
            <textarea
              value={(form.origin_keywords || []).join("\n")}
              onChange={(e) => setForm({ ...form, origin_keywords: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={4} placeholder="webinar&#10;seminario&#10;clase online"
              data-testid="agent-origin-keywords"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
            />
            <span className="text-[10px] text-carbon-500 mt-1 block">Si el lead trae alguna de estas palabras, se le asigna este origen automáticamente.</span>
          </label>
          <label className="block">
            <span className="section-label">Etapas donde aplica (stage_key, una por línea)</span>
            <textarea
              value={(form.origin_stage_keys || []).join("\n")}
              onChange={(e) => setForm({ ...form, origin_stage_keys: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={4} placeholder="new_lead&#10;candidate"
              data-testid="agent-origin-stage-keys"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
            />
            <span className="text-[10px] text-carbon-500 mt-1 block">Vacío = aplica en todas las etapas.</span>
          </label>
        </div>
      </Card>

      <Card className="p-5">
        <div className="section-label mb-2">Sandbox · probar prompt</div>
        <div className="flex gap-2 mb-3">
          <input value={testInput} onChange={(e) => setTestInput(e.target.value)} data-testid="agent-test-input" className="flex-1 bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          <button onClick={test} disabled={testing} data-testid="agent-test-btn" className="bg-forest text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-forest/90 disabled:opacity-60">
            <Play size={14} weight="fill" /> {testing ? "Probando..." : "Probar"}
          </button>
        </div>
        {testOutput && (
          <div className="bg-page border border-carbon-200 rounded p-3 text-sm">
            <div className="text-[10px] font-mono text-carbon-500 mb-1">
              {testOutput.provider}/{testOutput.model}
            </div>
            <div className="whitespace-pre-wrap" data-testid="agent-test-output">{testOutput.reply}</div>
          </div>
        )}
      </Card>
    </div>
  );
}

function CreateAgentModal({ models, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", description: "", system_prompt: "Eres un agente de Heroes Migrantes.",
    provider: "openai", model: "gpt-4o-mini", temperature: 0.7, active: true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/agents", form);
      toast.success("Agente creado");
      onCreated();
    } catch (err) { toast.error("Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl">Nuevo agente</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block"><span className="section-label">Nombre</span>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="section-label">Provider</span>
              <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value, model: (models[e.target.value] || [])[0] || "" })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
              </select></label>
            <label className="block"><span className="section-label">Modelo</span>
              <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono">
                {(models[form.provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="section-label">System prompt</span>
            <textarea required value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} rows={5} className="mt-1 w-full bg-carbon-900 text-carbon-100 border border-carbon-800 rounded px-3 py-2 text-sm font-mono" /></label>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover">{busy ? "Creando..." : "Crear"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
